import { FileIcon, PlusIcon } from "lucide-react";
import { useNavigate } from "react-router";

import { Column, EmptyState, Text } from "@/components";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { DemoConfig } from "@/types";

export const DemoConfigsTable = ({
  demoConfigs,
}: {
  demoConfigs: DemoConfig[];
}) => {
  const navigate = useNavigate();

  const handleRowClick = (demoConfig: DemoConfig) => {
    mixpanelUtils.trackRowClick("Demo config", MIXPANEL_SCREENS.DEMO_CONFIGS);
    void navigate(`/tools/demo-configs/${demoConfig.id}`);
  };

  return (
    <Column className="overflow-auto">
      <div className="rounded-t-lg border border-b-0 border-slate-200">
        <Table>
          <TableHeader>
            <TableRow className="border-none">
              <TableHead className="w-[25%]">
                <Text variant="header">Title</Text>
              </TableHead>
              <TableHead className="w-[20%]">
                <Text variant="header">Stevie Client</Text>
              </TableHead>
              <TableHead className="w-[20%]">
                <Text variant="header">Region</Text>
              </TableHead>
              <TableHead className="w-[15%]">
                <Text variant="header">Position</Text>
              </TableHead>
              <TableHead className="w-[20%]">
                <Text variant="header">Link</Text>
              </TableHead>
            </TableRow>
          </TableHeader>
        </Table>
      </div>
      <div className="overflow-y-auto rounded-b-lg border border-slate-200">
        <Table className="table-fixed">
          <TableBody>
            {demoConfigs.length === 0 ? (
              <TableRow>
                <TableCell className="p-0" colSpan={5}>
                  <EmptyState
                    className="bg-slate-50 py-14"
                    description="No demo configs were found. Add a demo config to get started!"
                    handlePrimaryClick={() => {
                      mixpanelUtils.trackButtonClick(
                        "Create demo config",
                        MIXPANEL_SCREENS.DEMO_CONFIGS,
                      );
                      void navigate("/tools/demo-configs/create");
                    }}
                    Icon={FileIcon}
                    PrimaryButtonIcon={PlusIcon}
                    primaryButtonName="Create demo config"
                    title="No demo configs found"
                  />
                </TableCell>
              </TableRow>
            ) : (
              demoConfigs.map((demoConfig) => (
                <TableRow
                  className="cursor-pointer"
                  key={demoConfig.id}
                  onClick={() => {
                    handleRowClick(demoConfig);
                  }}
                >
                  <TableCell className="w-[25%]">
                    <Text truncate>{demoConfig.title}</Text>
                  </TableCell>
                  <TableCell className="w-[20%]">
                    <Text truncate variant="secondary">
                      {demoConfig.config.stevie.client}
                    </Text>
                  </TableCell>
                  <TableCell className="w-[20%]">
                    <Text truncate variant="secondary">
                      {demoConfig.config.region}
                    </Text>
                  </TableCell>
                  <TableCell className="w-[15%]">
                    <Text truncate variant="secondary">
                      {demoConfig.config.position}
                    </Text>
                  </TableCell>
                  <TableCell className="w-[20%]">
                    <a
                      className="block truncate text-blue-600 hover:text-blue-800 hover:underline"
                      href={`https://demo.laborup.com/v/${demoConfig.version}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        mixpanelUtils.trackButtonClick(
                          "Open demo link",
                          MIXPANEL_SCREENS.DEMO_CONFIGS,
                        );
                      }}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      https://demo.laborup.com/v/{demoConfig.version}
                    </a>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </Column>
  );
};

